import React, { useMemo } from "react";
import { Link } from 'react-router-dom';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { CATEGORY_LANDINGS } from '../../config/siteContent';

const defaultSection = {
  enabled: true,
  title: 'Explorá por Categoría',
  subtitle: 'Encontrá la prenda perfecta para cada ocasión',
  maxItems: 6,
  items: [
    {
      _id: 'store-card',
      name: 'Tienda',
      description: 'Ver toda la colección',
      image: '/images/slider/slide1.png',
      link: '/tienda',
      cardType: 'store'
    },
    {
      _id: '1',
      name: 'Mujer',
      description: 'Indumentaria para mujer',
      image: '/images/slider/slide2.png',
      link: '/mujer',
      cardType: 'category'
    },
    {
      _id: '2',
      name: 'Hombre',
      description: 'Indumentaria para hombre',
      image: '/images/slider/slide3.png',
      link: '/hombre',
      cardType: 'category'
    },
    {
      _id: '3',
      name: 'Niños',
      description: 'Indumentaria para niños',
      image: '/images/slider/slide4.png',
      link: '/ninos',
      cardType: 'category'
    },
    {
      _id: '4',
      name: 'Ropa deportiva',
      description: 'Indumentaria deportiva',
      image: '/images/slider/slide3.png',
      link: '/ropa-deportiva',
      cardType: 'category'
    }
  ]
};

const OfferGrid = ({ featuredCategories }) => {
  const section = useMemo(() => {
    if (featuredCategories) {
      return {
        ...defaultSection,
        ...featuredCategories,
        items: Array.isArray(featuredCategories.items) && featuredCategories.items.length > 0
          ? featuredCategories.items
          : defaultSection.items
      };
    }

    return defaultSection;
  }, [featuredCategories]);

  const getCategoryLink = (category) => {
    if (category.link) return category.link;
    const landing = Object.values(CATEGORY_LANDINGS).find(
      (item) => item.categoryName === category.name
    );
    return landing?.path || (category.name === 'Tienda'
      ? '/tienda'
      : `/tienda?category=${encodeURIComponent(category.name)}`);
  };

  // Placeholder images for categories
  const getCategoryImage = (item) => {
    if (item?.image) {
      return getDisplayImageUrl(item.image);
    }

    const key = item?.name?.toLowerCase() || '';
    if (!key) {
      return '/images/slider/slide1.png';
    }

    const images = {
      'tienda': '/images/slider/slide1.png',
      'mujer': '/images/slider/slide2.png',
      'hombre': '/images/slider/slide3.png',
      'niños': '/images/slider/slide4.png',
      'ropa deportiva': '/images/slider/slide3.png'
    };
    return images[key] || '/images/slider/slide1.png';
  };

  if (section.enabled === false) {
    return null;
  }

  const displayCategories = section.items.slice(0, section.maxItems || 6);

  return (
    <section style={{
      padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 40px)',
      background: 'var(--bg-white)'
    }}>
      {/* Section Header */}
      <div style={{ textAlign: 'center', marginBottom: 'clamp(30px, 5vw, 50px)' }}>
        <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
          fontWeight: 300,
          textTransform: 'uppercase',
          letterSpacing: '0.15em',
          color: 'var(--text-primary)',
          marginBottom: '8px'
        }}>
          {section.title}
        </h2>
        <p style={{
          color: 'var(--text-muted)',
          fontSize: '0.9rem',
          letterSpacing: '0.03em'
        }}>
          {section.subtitle}
        </p>
      </div>

      {/* Category Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: displayCategories.length <= 3
          ? 'repeat(auto-fit, minmax(280px, 1fr))'
          : 'repeat(3, 1fr)',
        gap: '4px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {displayCategories.map((category, index) => (
          <Link
            key={category._id}
            to={getCategoryLink(category)}
            style={{
              position: 'relative',
              aspectRatio: index < 3 ? '4/5' : '4/5',
              overflow: 'hidden',
              textDecoration: 'none',
              display: 'block'
            }}
          >
            {/* Background Image */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundImage: `url('${getCategoryImage(category)}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
              className="cat-bg-img"
            />

            {/* Overlay */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 60%)',
              transition: 'background 0.3s ease'
            }} />

            {/* Content */}
            <div style={{
              position: 'absolute',
              bottom: '24px',
              left: '24px',
              right: '24px',
              zIndex: 2
            }}>
              <h3 style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.85rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: '#ffffff',
                margin: 0,
                textShadow: '0 1px 4px rgba(0,0,0,0.3)'
              }}>
                {category.name}
              </h3>
            </div>
          </Link>
        ))}
      </div>

      <style>{`
        a:hover .cat-bg-img {
          transform: scale(1.06);
        }
        @media (max-width: 768px) {
          section > div:last-of-type {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 480px) {
          section > div:last-of-type {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default OfferGrid;
